<script setup lang="ts">
import { useListingError } from '#layers/listing/composables/use-listing-error';
import { ButtonRetry } from '#layers/ui/public';

const {
	error,
	canResetView,
} = defineProps<{
	/** Anything `ErrorDetailDialog` can format: an `Error`, an engine error payload, or a plain message. */
	error: unknown;
	/** Whether the view carries unsaved changes, making a rollback something to offer. */
	canResetView?: boolean;
}>();

defineEmits<{
	retry: [];
	resetView: [];
}>();

const { icon, title, description, showRetry, showResetView, showDetails } = useListingError({
	error: () => error,
	canResetView: () => canResetView,
});
</script>

<template>
	<div
		class="flex flex-1 items-center justify-center"
		data-testid="listing-error-state"
	>
		<DErrorState
			:icon
			:title
			:description
		>
			<template #action>
				<!-- `mt-2` widens the inherited `gap-6` to 32px: the largest gap in the stack belongs
					before the actions, so they read as a group rather than a fourth line of content. -->
				<div class="mt-2 flex flex-col items-center gap-3">
					<div
						v-if="showRetry || showResetView"
						class="flex items-center gap-2"
					>
						<ButtonRetry
							v-if="showRetry"
							data-testid="listing-error-retry"
							@click="$emit('retry')"
						/>
						<DButton
							v-if="showResetView"
							variant="outline"
							color="neutral"
							size="sm"
							icon="lucide:rotate-ccw"
							:label="$t('errors.listing.reset_view')"
							data-testid="listing-error-reset-view"
							@click="$emit('resetView')"
						/>
					</div>
					<!-- Same size and weight as the buttons above would read as a third one that lost its
						border, so this recedes to muted to land as the tertiary action it is. -->
					<DButton
						variant="ghost"
						color="neutral"
						size="sm"
						class="font-normal text-muted"
						:label="$t('common.view_details')"
						data-testid="listing-error-details"
						@click="showDetails"
					/>
				</div>
			</template>
		</DErrorState>
	</div>
</template>
